// サーバーサイドレンダリング用の純粋関数群。DOM に触らず、文字列だけを組み立てる。
// worker.ts（/m/{code}・/pref/{code}・/about の SSR）と main.ts（クライアント側の詳細ドロワー）が
// renderMuniDetailBody を共有するので、詳細の見た目は 1 箇所にしか無い。
// 文言は lang 引数で切り替える。既定は日本語なので、言語を渡さない呼び出しは従来どおり。
import type { Manifest, Muni } from "../../schema/muni";
import { CLIMATE_TEMP_DOMAIN, colorFor, DETAIL_TILE_DOMAINS } from "./colorScale";
import { CONTACT_LIMITS, CONTACT_TOPICS, HONEYPOT_FIELD } from "./contact";
import { describeFlags } from "./flags";
import { akiyaBankLinks, disaportalDeepLink } from "./hazardLinks";
import { ICON_EXTERNAL, ICON_EXTERNAL_INLINE, ICON_LOGO } from "./icons";
import {
  DASH,
  DEFAULT_LANG,
  formatNumber,
  LANG_NAMES,
  LANGS,
  otherLang,
  t,
  withLang,
  type Lang,
  type MetricSummary,
} from "./i18n";
import { snapshotOf } from "./home";
import { isStale, sourceForField } from "./manifestUtils";
import { getMetrics, isRankable, LAND_PRICE_MIN_SAMPLE, METRIC_ORDER, yen, type MetricKey } from "./metrics";
import { muniLabel, prefLabel } from "./prefNames";
import { percentileBelow, rankLabel, type RankIndex } from "./rank";
import type { Neighbor } from "./geo";
import { prefCodeOf, type PrefEntry } from "./prefs";

export function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function pctOrDash(v: number | null): string {
  return v === null ? DASH : `${(v * 100).toFixed(1)}%`;
}

function yenOrDash(v: number | null, lang: Lang): string {
  return v === null ? DASH : yen(v, lang);
}

function signedPercent(ratio: number): string {
  // 増える場合だけ + を付ける。丸めた結果が 0 なら -0.0% にせず 0.0% と出す。
  const rounded = Number((ratio * 100).toFixed(1));
  return `${rounded > 0 ? "+" : ""}${rounded.toFixed(1)}%`;
}

function clampPercent(ratio: number): number {
  return Math.min(100, Math.max(0, ratio * 100));
}

/** フィールドの出典チップ。ネイティブ Popover API を使うため JS 不要で開閉できる。 */
function sourceChip(manifest: Manifest, fieldPath: string, uid: string, lang: Lang): string {
  const source = sourceForField(manifest, fieldPath);
  if (!source) return "";
  const m = t(lang);
  const popoverId = `src-${uid}`;
  const staleNote = isStale(source) ? `<p class="stale-note">${escapeHtml(m.staleNote)}</p>` : "";
  return `<button type="button" class="src-chip" popovertarget="${popoverId}" aria-label="${escapeHtml(m.showSource)}">${escapeHtml(m.source)}</button>
    <div id="${popoverId}" popover class="src-popover">
      <p class="src-title">${escapeHtml(source.title)}</p>
      <p class="src-meta">${escapeHtml(m.sourceMeta(source.publisher, source.retrievedAt))}</p>
      <p class="src-method">${escapeHtml(source.method)}</p>
      <a href="${escapeHtml(source.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(source.url)}</a>
      ${staleNote}
    </div>`;
}

/**
 * 現住地（home.ts）との比較行を差し込む口。値を表示している要素に data-metric / data-value を付け、
 * その中に空の .tile-home を 1 つ置く。SSR は localStorage を読めないので中身は空のまま返し、
 * app/dist/home.js（ui/home.ts）が読み込み後に埋める。欠測は data-value="" で表す。
 */
function homeSlotAttrs(metric: MetricKey, value: number | null): string {
  return ` data-metric="${metric}" data-value="${value === null ? "" : escapeHtml(String(value))}"`;
}

const HOME_LINE = `<p class="tile-home" hidden></p>`;

/**
 * 詳細の先頭に置く現住地バー。SSR では文言もボタンの状態も決められないので、
 * その町を現住地にするのに必要な素材（識別子と 12 指標の値）だけを data 属性で渡す。
 * 値をここに丸ごと載せておくと、/m/{code} で「現住地にする」を押しても
 * データを取りに行かずにスナップショットを作れる。
 */
function homeBar(muni: Muni, manifest: Manifest, lang: Lang): string {
  const m = t(lang);
  const values = snapshotOf(muni, manifest.builtAt).values;
  return `<div class="home-bar" data-code="${escapeHtml(muni.code)}" data-name="${escapeHtml(muni.name)}" data-pref="${escapeHtml(muni.pref)}" data-built-at="${escapeHtml(manifest.builtAt)}" data-values="${escapeHtml(JSON.stringify(values))}">
      <p class="home-bar-text">${escapeHtml(m.homeIntro)}</p>
      <div class="home-bar-actions">
        <button type="button" class="btn-ghost home-set">${escapeHtml(m.homeSet)}</button>
        <button type="button" class="btn-ghost home-clear" hidden>${escapeHtml(m.homeClear)}</button>
      </div>
    </div>`;
}

interface HazardTile {
  metric: Extract<MetricKey, "flood" | "tsunami" | "sediment" | "quake">;
  fieldPath: string;
  label: string;
  value: number | null;
  /** 津波の「指定なし」のように、欠測ではなく確定した状態を表す文言。 */
  overrideText?: string;
}

function hazardTiles(muni: Muni, lang: Lang): HazardTile[] {
  const m = t(lang);
  const noTsunami = muni.flags.includes("inland-no-tsunami");
  return [
    {
      metric: "flood",
      fieldPath: "hazard.floodL2Ratio",
      label: m.tileFlood,
      value: muni.hazard.floodL2Ratio,
    },
    {
      metric: "tsunami",
      fieldPath: "hazard.tsunamiRatio",
      label: m.tileTsunami,
      value: noTsunami ? null : muni.hazard.tsunamiRatio,
      ...(noTsunami ? { overrideText: m.notDesignated } : {}),
    },
    {
      metric: "sediment",
      fieldPath: "hazard.sedimentRatio",
      label: m.tileSediment,
      value: muni.hazard.sedimentRatio,
    },
    {
      metric: "quake",
      fieldPath: "hazard.quakeProb30y",
      label: m.tileQuake,
      value: muni.hazard.quakeProb30y,
    },
  ];
}

function renderTile(tile: HazardTile, manifest: Manifest, muni: Muni, lang: Lang, rank?: RankIndex): string {
  const domain = DETAIL_TILE_DOMAINS[tile.metric];
  const color = colorFor(tile.value, domain, "risk");
  const valueText = tile.overrideText ?? pctOrDash(tile.value);

  // 順位が引ければ全国分布上の位置を、引けなければ固定 domain 上の位置をマーカーにする。
  const hasValue = tile.value !== null && tile.overrideText === undefined;
  const markerPercent = !hasValue
    ? null
    : rank
      ? clampPercent(percentileBelow(rank, tile.metric, tile.value as number))
      : clampPercent(((tile.value as number) - domain[0]) / (domain[1] - domain[0]));

  const rankHtml =
    hasValue && rank
      ? `<p class="tile-rank">${escapeHtml(rankLabel(rank, tile.metric, tile.value as number, lang))}</p>`
      : `<p class="tile-rank tile-rank-empty" aria-hidden="true"></p>`;

  // マーカーは percentileBelow（左 = 低い値）で置くので、両端の言葉と色の向きも
  // 「左 = 低い値」に合わせる。望ましい端が低い側なら良い色は左、高い側なら右。
  const def = getMetrics(lang)[tile.metric];
  const goodLeft = def.better === "low";
  const [leftWord, rightWord] = goodLeft ? [def.betterWord, def.worseWord] : [def.worseWord, def.betterWord];
  const trackHtml =
    markerPercent === null
      ? ""
      : `<div class="tile-track ${goodLeft ? "tile-track--good-left" : "tile-track--good-right"}"><span style="left: ${markerPercent.toFixed(1)}%"></span></div>
      <span class="tile-track-ends"><i>${escapeHtml(leftWord)}</i><i>${escapeHtml(rightWord)}</i></span>`;

  return `<div class="tile" style="--tile-color: ${escapeHtml(color)}"${homeSlotAttrs(tile.metric, tile.value)}>
      <p class="tile-label">${escapeHtml(tile.label)}${sourceChip(manifest, tile.fieldPath, `${muni.code}-${tile.metric}`, lang)}</p>
      <p class="tile-value">${escapeHtml(valueText)}</p>
      ${rankHtml}
      ${HOME_LINE}
      ${trackHtml}
    </div>`;
}

function hazardNote(muni: Muni, lang: Lang): string {
  const m = t(lang);
  const link = disaportalDeepLink(muni);
  return `<p class="hazard-note">${escapeHtml(m.hazardNote)}
    <a href="${escapeHtml(link)}" target="_blank" rel="noopener noreferrer">${escapeHtml(m.hazardNoteLink)} ${ICON_EXTERNAL}</a></p>`;
}

const CHART_WIDTH = 360;
const CHART_HEIGHT = 100;
const CHART_TOP = 18; // 上 18px は hover/tap 時の数値ラベル用の余白
const CHART_BOTTOM = 78; // ここから下は月ラベルの帯
const BAR_WIDTH = 16;
const VALUE_LABEL_GAP = 4;
const VALUE_LABEL_HALF_WIDTH = 22; // "-8.5℃" 程度の幅の半分。端の月でラベルが SVG 外に出ないよう x をこの範囲に収める

/**
 * 12 か月の平均気温を縦棒で描く。0℃ の基準線を引き、棒は thermal スケールで塗る。
 * 各月は tabindex 付きの <g> で、PC は :hover、モバイルはタップ（focus）で数値ラベルが出る（CSS のみ、JS 不要）。
 * 数値ラベルは常に棒の上端（負の値なら 0℃ 線）の上に置くので、月ラベルと重ならない。
 */
function climateChart(muni: Muni, lang: Lang): string {
  const m = t(lang);
  const temps = muni.tempMonthlyC;
  const known = temps.filter((v): v is number => v !== null && Number.isFinite(v));
  if (known.length === 0) return `<p class="md-value">${DASH}</p>`;

  const lo = Math.min(0, ...known) - 1;
  const hi = Math.max(0, ...known) + 1;
  const span = hi - lo;
  const yOf = (v: number): number => CHART_BOTTOM - ((v - lo) / span) * (CHART_BOTTOM - CHART_TOP);
  const zeroY = yOf(0);
  const slot = CHART_WIDTH / 12;

  const bars = temps
    .map((v, i) => {
      const cx = slot * i + slot / 2;
      const label = `<text class="chart-month" x="${cx.toFixed(1)}" y="${CHART_HEIGHT - 4}" text-anchor="middle">${escapeHtml(lang === "ja" ? String(i + 1) : m.monthShort(i + 1))}</text>`;
      if (v === null || !Number.isFinite(v)) return label;
      const y = yOf(v);
      const top = Math.min(y, zeroY);
      const height = Math.max(1.5, Math.abs(zeroY - y));
      const fill = colorFor(v, CLIMATE_TEMP_DOMAIN, "thermal");
      const valueText = `${v.toFixed(1)}℃`;
      const labelX = Math.min(Math.max(cx, VALUE_LABEL_HALF_WIDTH), CHART_WIDTH - VALUE_LABEL_HALF_WIDTH);
      return `<g class="chart-bar" tabindex="0" role="img" aria-label="${escapeHtml(m.monthTemp(i + 1, valueText))}">
        <rect class="chart-hit" x="${(slot * i).toFixed(1)}" y="0" width="${slot.toFixed(1)}" height="${CHART_HEIGHT}" />
        <rect x="${(cx - BAR_WIDTH / 2).toFixed(1)}" y="${top.toFixed(1)}" width="${BAR_WIDTH}" height="${height.toFixed(1)}" rx="3" fill="${escapeHtml(fill)}" />
        <text class="chart-value" x="${labelX.toFixed(1)}" y="${(top - VALUE_LABEL_GAP).toFixed(1)}" text-anchor="middle">${valueText}</text>
      </g>${label}`;
    })
    .join("");

  return `<svg class="climate-chart" viewBox="0 0 ${CHART_WIDTH} ${CHART_HEIGHT}" role="group" aria-label="${escapeHtml(m.climateChartLabel)}" preserveAspectRatio="none">
      <line class="chart-zero" x1="0" y1="${zeroY.toFixed(1)}" x2="${CHART_WIDTH}" y2="${zeroY.toFixed(1)}" />
      ${bars}
    </svg>`;
}

const DIST_BAR_MAX_KM = 1000;

function distanceRows(muni: Muni, manifest: Manifest, lang: Lang): string {
  const m = t(lang);
  const rows: [MetricKey, string, number][] = [
    ["distTokyo", m.stationTokyo, muni.distKm.tokyo],
    ["distOsaka", m.stationOsaka, muni.distKm.osaka],
    ["distNagoya", m.stationNagoya, muni.distKm.nagoya],
  ];
  return rows
    .map(
      ([metric, label, km]) => `<div class="dist-row"${homeSlotAttrs(metric, km)}>
        <span class="dist-label">${escapeHtml(label)}</span>
        <span class="dist-bar"><span style="width: ${clampPercent(km / DIST_BAR_MAX_KM).toFixed(1)}%"></span></span>
        <span class="dist-value">${km.toFixed(1)} km</span>
        ${HOME_LINE}
      </div>`,
    )
    .join("")
    .concat(
      `<p class="md-note">${escapeHtml(m.greatCircleNote)}${sourceChip(manifest, "distKm", `${muni.code}-dist`, lang)}</p>`,
    );
}

function landPriceBlock(muni: Muni, manifest: Manifest, lang: Lang, rank?: RankIndex): string {
  const m = t(lang);
  const value = muni.landPriceMedianYenM2;
  const sample =
    value !== null ? `<p class="md-note">${escapeHtml(m.landPriceSample(muni.landPriceSampleN))}</p>` : "";
  const rankRow =
    value === null || !rank
      ? ""
      : isRankable("landPrice", muni)
        ? `<p class="md-rank">${escapeHtml(rankLabel(rank, "landPrice", value, lang))}</p>`
        : `<p class="md-rank">${escapeHtml(m.landPriceLowSampleRank(LAND_PRICE_MIN_SAMPLE))}</p>`;
  return `<section class="md-block"${homeSlotAttrs("landPrice", value)}>
      <h2>${escapeHtml(m.landPriceHeading)}${sourceChip(manifest, "landPriceMedianYenM2", `${muni.code}-land`, lang)}</h2>
      <p class="md-value">${escapeHtml(yenOrDash(value, lang))}</p>
      ${rankRow}
      ${HOME_LINE}
      ${sample}
    </section>`;
}

interface LivingRow {
  label: string;
  /** 出典チップを引くためのフィールドパス。行ごとに出典が違う（人口 = 国勢調査 / 食料品店 = 経済センサス基礎調査）。 */
  fieldPath: string;
  value: string;
  /** 補足（人口密度・高齢化率・人口あたり件数のような導出値）。 */
  note?: string;
  rank?: string;
  /** 12 指標のどれかを表している行は、現住地との比較行の口を持つ。 */
  metric?: MetricKey;
  metricValue?: number | null;
}

/** ラベル / 値 / 補足 / 順位を 1 行に並べる共有マークアップ。 */
function liveRowsHtml(rows: LivingRow[], muni: Muni, manifest: Manifest, lang: Lang): string {
  const inner = rows
    .map(
      (r) => `<div class="live-row"${r.metric ? homeSlotAttrs(r.metric, r.metricValue ?? null) : ""}>
        <span class="live-label">${escapeHtml(r.label)}${sourceChip(manifest, r.fieldPath, `${muni.code}-${r.fieldPath}`, lang)}</span>
        <span class="live-value">${escapeHtml(r.value)}</span>
        <span class="live-note">${escapeHtml(r.note ?? "")}</span>
        <span class="live-rank">${escapeHtml(r.rank ?? "")}</span>
        ${r.metric ? HOME_LINE : ""}
      </div>`,
    )
    .join("");
  return `<div class="live-rows">${inner}</div>`;
}

/** 施設数を人口 1 万人あたりに直した注記。生値か人口が無ければ注記そのものを出さない。 */
function perTenThousandNote(count: number | null, pop: number | null, unit: string, lang: Lang): string | null {
  if (count === null || pop === null || pop <= 0) return null;
  return t(lang).perTenThousand(((count / pop) * 10000).toFixed(1), unit);
}

/**
 * 人口密度・高齢化率・人口の増減率は data には持たせず、
 * 生値（総人口 / 65 歳以上人口 / 面積 / 将来推計人口）から表示の直前にここだけで割る。
 * 新しいフィールドが無い古い data/ でも `?? null` で受けて壊れないようにする。
 */
function populationRows(muni: Muni, manifest: Manifest, lang: Lang, rank?: RankIndex): LivingRow[] {
  const m = t(lang);
  const pop = muni.populationTotal ?? null;
  const aged = muni.populationAged65 ?? null;
  const future = muni.populationFuture ?? null;
  const peopleOrDash = (v: number | null) => (v === null ? DASH : m.people(v));

  const density = pop !== null && muni.areaKm2 > 0 ? pop / muni.areaKm2 : null;
  const agedRatio = pop !== null && pop > 0 && aged !== null ? aged / pop : null;
  // 知りたいのは「どれだけ減るか」なので、値の欄には率を出し、人数は注記に回す。
  const changeRatio = pop !== null && pop > 0 && future !== null ? (future - pop) / pop : null;
  const years = manifest.populationYears;
  const changeNote =
    changeRatio === null
      ? null
      : years
        ? m.futureNote(years.base, peopleOrDash(pop), years.future, peopleOrDash(future))
        : m.futureNoteNoYears(peopleOrDash(pop), peopleOrDash(future));

  return [
    {
      label: m.rowPopulation,
      fieldPath: "populationTotal",
      metric: "population",
      metricValue: pop,
      value: peopleOrDash(pop),
      ...(density !== null ? { note: m.densityNote(density) } : {}),
      ...(pop !== null && rank ? { rank: rankLabel(rank, "population", pop, lang) } : {}),
    },
    {
      label: m.rowAged65,
      fieldPath: "populationAged65",
      value: peopleOrDash(aged),
      ...(agedRatio !== null ? { note: m.agedRatioNote((agedRatio * 100).toFixed(1)) } : {}),
    },
    {
      label: m.rowFutureChange,
      fieldPath: "populationFuture",
      value: changeRatio === null ? DASH : signedPercent(changeRatio),
      ...(changeNote !== null ? { note: changeNote } : {}),
    },
  ];
}

/** 病院・診療所・食料品店という「歩いて届く範囲にあるもの」の数。0 は「0 か所」、null は「—」で区別する。 */
function facilityRows(muni: Muni, lang: Lang, rank?: RankIndex): LivingRow[] {
  const m = t(lang);
  const pop = muni.populationTotal ?? null;
  const hospitals = muni.hospitalCount ?? null;
  const clinics = muni.clinicCount ?? null;
  const stores = muni.foodStoreCount ?? null;
  const placesOrDash = (v: number | null) => (v === null ? DASH : m.places(v));

  const hospitalNote = perTenThousandNote(hospitals, pop, m.unitPlaces, lang);
  const clinicNote = perTenThousandNote(clinics, pop, m.unitPlaces, lang);
  const storeNote = perTenThousandNote(stores, pop, m.unitStores, lang);

  return [
    {
      label: m.rowHospitals,
      fieldPath: "hospitalCount",
      value: placesOrDash(hospitals),
      ...(hospitalNote !== null ? { note: hospitalNote } : {}),
    },
    {
      label: m.rowClinics,
      fieldPath: "clinicCount",
      value: placesOrDash(clinics),
      ...(clinicNote !== null ? { note: clinicNote } : {}),
    },
    {
      label: m.rowStores,
      fieldPath: "foodStoreCount",
      metric: "foodStores",
      metricValue: stores,
      value: stores === null ? DASH : m.stores(stores),
      ...(storeNote !== null ? { note: storeNote } : {}),
      ...(stores !== null && rank ? { rank: rankLabel(rank, "foodStores", stores, lang) } : {}),
    },
  ];
}

/** 人口の規模・年齢構成・これからの増減。 */
function populationBlock(muni: Muni, manifest: Manifest, lang: Lang, rank?: RankIndex): string {
  return `<section class="md-block">
      <h2>${escapeHtml(t(lang).populationHeading)}</h2>
      ${liveRowsHtml(populationRows(muni, manifest, lang, rank), muni, manifest, lang)}
    </section>`;
}

/** 医療機関と買い物先。数そのものは人口規模に引きずられるので人口あたりを併記する。 */
function facilitiesBlock(muni: Muni, manifest: Manifest, lang: Lang, rank?: RankIndex): string {
  const m = t(lang);
  return `<section class="md-block">
      <h2>${escapeHtml(m.facilitiesHeading)}</h2>
      ${liveRowsHtml(facilityRows(muni, lang, rank), muni, manifest, lang)}
      <p class="md-note">${escapeHtml(m.facilitiesNote)}</p>
    </section>`;
}

function climateBlock(muni: Muni, manifest: Manifest, lang: Lang): string {
  const m = t(lang);
  const jan = muni.tempMonthlyC[0];
  const aug = muni.tempMonthlyC[7];
  const parts = [
    m.monthTemp(1, jan === null || jan === undefined ? DASH : `${jan.toFixed(1)}℃`),
    m.monthTemp(8, aug === null || aug === undefined ? DASH : `${aug.toFixed(1)}℃`),
  ];
  // 最深積雪は注記に埋もれていたので独立行にする。0 は「積もらない」で、欠測の「—」とは意味が違う。
  const snow = muni.snowDepthMaxCm ?? null;
  const snowRow: LivingRow = {
    label: m.maxSnowDepthRow,
    fieldPath: "snowDepthMaxCm",
    value: snow === null ? DASH : `${formatNumber(snow, lang)} cm`,
  };
  return `<section class="md-block">
      <h2>${escapeHtml(m.climateHeading)}${sourceChip(manifest, "tempMonthlyC", `${muni.code}-temp`, lang)}</h2>
      ${climateChart(muni, lang)}
      ${liveRowsHtml([snowRow], muni, manifest, lang)}
      <p class="md-note">${escapeHtml(parts.join(m.sep))}</p>
      <div class="home-slot"${homeSlotAttrs("tempJan", jan ?? null)}>${HOME_LINE}</div>
      <div class="home-slot"${homeSlotAttrs("tempAug", aug ?? null)}>${HOME_LINE}</div>
    </section>`;
}

/**
 * 公式の空き家バンク URL が無い自治体では、代替リンクを公式と同じ見た目で出さない。
 * 擬似インタビューで、検索フォールバックを公式導線と誤認した人が「自分で打つのと同じ」と
 * 感じ、サイト全体の信用を割り引いていた（KaizenLab V4）。未収録であることを先に明示する。
 */
function akiyaBlock(muni: Muni, lang: Lang): string {
  const m = t(lang);
  const { primary, nationwide } = akiyaBankLinks(muni, lang);
  const unlisted = primary.target !== "local";
  const nationwideHtml = nationwide
    .map(
      (l) =>
        `<a href="${escapeHtml(l.url)}" target="_blank" rel="noopener noreferrer" data-akiya-target="nationwide">${escapeHtml(l.label)}</a>`,
    )
    .join(" / ");
  const contactHref = escapeHtml(withLang("/contact?topic=akiya-bank-url", lang));
  const unlistedHtml = unlisted
    ? `<p class="akiya-unlisted">${escapeHtml(m.akiyaUnlistedNote)}</p>`
    : "";
  const unlistedTipHtml = unlisted
    ? `<p class="akiya-unlisted-tip">${escapeHtml(m.akiyaUnlistedTipBefore)}<a href="${contactHref}">${escapeHtml(m.akiyaUnlistedTipLink)}</a>${escapeHtml(m.akiyaUnlistedTipAfter)}</p>`
    : "";
  return `<section class="md-cta">
      ${unlistedHtml}
      <a class="${unlisted ? "btn-ghost" : "btn-primary"}" href="${escapeHtml(primary.url)}" target="_blank" rel="noopener noreferrer" data-akiya-target="${primary.target}">${escapeHtml(primary.label)} ${ICON_EXTERNAL}</a>
      ${unlistedTipHtml}
      <p class="nationwide">${escapeHtml(m.akiyaNationwide)}: ${nationwideHtml}</p>
    </section>`;
}

/** /m/{code} の SSR ページと、クライアント側の詳細ドロワーで共有する本体 HTML。 */
export function renderMuniDetailBody(
  muni: Muni,
  manifest: Manifest,
  rank?: RankIndex,
  lang: Lang = DEFAULT_LANG,
): string {
  const m = t(lang);
  const tilesHtml = hazardTiles(muni, lang)
    .map((tile) => renderTile(tile, manifest, muni, lang, rank))
    .join("");

  const flagDescriptions = describeFlags(muni.flags, lang);
  const flagsHtml = flagDescriptions.length
    ? `<ul class="flags">${flagDescriptions.map((d) => `<li>${escapeHtml(d)}</li>`).join("")}</ul>`
    : "";

  return `<article class="muni-detail" data-code="${escapeHtml(muni.code)}">
      <header class="md-head">
        <p class="md-pref">${escapeHtml(prefLabel(muni.pref, lang))}</p>
        <h1 class="md-name">${escapeHtml(muni.name)}</h1>
        <p class="md-sub">${escapeHtml(m.muniSubtitle(formatNumber(muni.areaKm2, lang), muni.distKm.tokyo.toFixed(1)))}</p>
      </header>
      ${homeBar(muni, manifest, lang)}
      <section class="md-block md-hazard-section">
        <h2>${escapeHtml(m.hazardHeading(muni.name))}</h2>
        <div class="md-tiles" aria-label="${escapeHtml(m.disasterRisk)}">${tilesHtml}</div>
        ${hazardNote(muni, lang)}
      </section>
      ${landPriceBlock(muni, manifest, lang, rank)}
      ${populationBlock(muni, manifest, lang, rank)}
      ${facilitiesBlock(muni, manifest, lang, rank)}
      ${climateBlock(muni, manifest, lang)}
      <section class="md-block">
        <h2>${escapeHtml(m.distanceHeading)}</h2>
        ${distanceRows(muni, manifest, lang)}
      </section>
      ${akiyaBlock(muni, lang)}
      ${flagsHtml}
    </article>`;
}


export interface PageOptions {
  gaMeasurementId: string;
  adsenseClient: string;
  /**
   * canonical / og:url / JSON-LD の絶対 URL を組み立てる基準（末尾スラッシュ無し）。
   * worker.ts の siteOrigin() が SITE_ORIGIN か、リクエストの origin を入れる。
   */
  siteOrigin: string;
  /**
   * 利用者が URL で言語を指定して来たか。指定して来たならサイト内リンクにも
   * lang= を引き継ぎ、ブラウザの言語設定で上書きされないようにする。
   */
  explicitLang?: boolean;
}

/**
 * トップの title / description。日本語版は app/public/index.html の
 * <title> / meta[name=description]（I18N_HEAD マーカーの中）と同じ文言でなければならない
 * （Worker が言語に応じて差し替え、canonical と OGP もここから注入するため）。
 */
export const TOP_TITLE = t(DEFAULT_LANG).siteTitle;
export const TOP_DESCRIPTION = t(DEFAULT_LANG).siteDescription;

export function gtagSnippet(gaMeasurementId: string): string {
  if (!gaMeasurementId) return "";
  const id = escapeHtml(gaMeasurementId);
  return `
    <script async src="https://www.googletagmanager.com/gtag/js?id=${id}"></script>
    <script>
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${id}');
    </script>`;
}

/**
 * /m/{code} には main.ts の SPA が乗らないため、詳細閲覧と空き家バンククリックを
 * gtag 直書きで計測する。仮説 001（防災移住マップ）の「詳細閲覧 → 空き家バンククリック」
 * ファネルの 2 ステップにあたり、main.ts の track("municipality_view"/"akiya_bank_click", ...)
 * と同じイベント名・{code, pref} を送る（pref は JIS 5 桁コードの先頭 2 桁）。
 * gtagSnippet が定義する window.gtag より後に読み込むこと。
 */
export function muniTrackingSnippet(gaMeasurementId: string, code: string): string {
  if (!gaMeasurementId) return "";
  const codeJson = JSON.stringify(code).replace(/</g, "\\u003c");
  const prefJson = JSON.stringify(code.slice(0, 2)).replace(/</g, "\\u003c");
  return `
    <script>
      (function(){
        var code = ${codeJson};
        var pref = ${prefJson};
        if (typeof gtag === "function") gtag("event", "municipality_view", { code: code, pref: pref });
        document.addEventListener("click", function(ev){
          var link = ev.target instanceof Element ? ev.target.closest("a[data-akiya-target]") : null;
          if (!link || typeof gtag !== "function") return;
          gtag("event", "akiya_bank_click", { code: code, pref: pref, target: link.dataset.akiyaTarget || "" });
        });
      })();
    </script>`;
}

export function adsenseLoaderSnippet(adsenseClient: string): string {
  if (!adsenseClient) return "";
  const client = escapeHtml(adsenseClient);
  return `<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${client}" crossorigin="anonymous"></script>`;
}

/** JSON-LD。`<` を \u003c にして、値の中の "</script>" が script を閉じられないようにする。 */
function jsonLdScripts(items: object[]): string {
  return items
    .map(
      (item) =>
        `<script type="application/ld+json">${JSON.stringify(item).replace(/</g, "\\u003c")}</script>`,
    )
    .join("\n  ");
}

export interface Crumb {
  name: string;
  path: string;
}

export function breadcrumbJsonLd(siteOrigin: string, crumbs: Crumb[]): object {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${siteOrigin}${c.path}`,
    })),
  };
}

/** パンくずの HTML。最後の 1 つは現在地なのでリンクにしない。 */
export function breadcrumbHtml(crumbs: Crumb[], lang: Lang, explicitLang: boolean): string {
  const items = crumbs
    .map((c, i) =>
      i === crumbs.length - 1
        ? `<li aria-current="page">${escapeHtml(c.name)}</li>`
        : `<li><a href="${escapeHtml(withLang(c.path, lang, explicitLang))}">${escapeHtml(c.name)}</a></li>`,
    )
    .join("");
  return `<nav class="breadcrumb" aria-label="${escapeHtml(t(lang).breadcrumb)}"><ol>${items}</ol></nav>`;
}

/** 文章の途中に挟むサイト内リンク。言語の引き継ぎとエスケープを 1 箇所にまとめる。 */
function internalLink(path: string, label: string, lang: Lang, explicitLang: boolean): string {
  return `<a href="${escapeHtml(withLang(path, lang, explicitLang))}">${escapeHtml(label)}</a>`;
}

/** 外部サイトへのリンク。新しいタブで開き、参照元は渡さない。 */
function externalLink(url: string, label: string): string {
  return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}${ICON_EXTERNAL_INLINE}</a>`;
}

const OG_IMAGE_PATH = "/og.png";
const OG_IMAGE_WIDTH = 1200;
const OG_IMAGE_HEIGHT = 630;

interface SeoHead {
  siteOrigin: string;
  /** canonical の元になるパス（言語指定は付けない）。トップは "/"。 */
  path: string;
  lang: Lang;
  title: string;
  description: string;
  ogType: "website" | "article";
  jsonLd: object[];
}

/**
 * 同じ内容の別言語版を検索エンジンに知らせる。日本語版は ?lang= の付かない URL なので、
 * x-default も日本語版に向ける（言語指定なしのアクセスは Accept-Language で振り分ける）。
 */
export function hreflangLinks(path: string, origin = ""): string {
  const alternates = LANGS.map(
    (l) => `<link rel="alternate" hreflang="${l}" href="${escapeHtml(origin + withLang(path, l))}" />`,
  );
  return [...alternates, `<link rel="alternate" hreflang="x-default" href="${escapeHtml(origin + path)}" />`].join(
    "\n  ",
  );
}

/** canonical・hreflang・OGP・Twitter Card・JSON-LD。pageShell と envInjectSnippet が共有する。 */
function seoHeadTags(seo: SeoHead): string {
  const m = t(seo.lang);
  // canonical は「その言語のページ」を指す。別言語版は hreflang で並べる。
  const url = escapeHtml(`${seo.siteOrigin}${withLang(seo.path, seo.lang)}`);
  const title = escapeHtml(seo.title);
  const description = escapeHtml(seo.description);
  return `<link rel="canonical" href="${url}" />
  ${hreflangLinks(seo.path, seo.siteOrigin)}
  <meta property="og:type" content="${seo.ogType}" />
  <meta property="og:site_name" content="${escapeHtml(m.brand)}" />
  <meta property="og:title" content="${title}" />
  <meta property="og:description" content="${description}" />
  <meta property="og:url" content="${url}" />
  <meta property="og:locale" content="${escapeHtml(m.ogLocale)}" />
  <meta property="og:image" content="${escapeHtml(`${seo.siteOrigin}${OG_IMAGE_PATH}`)}" />
  <meta property="og:image:width" content="${OG_IMAGE_WIDTH}" />
  <meta property="og:image:height" content="${OG_IMAGE_HEIGHT}" />
  <meta name="twitter:card" content="summary_large_image" />
  ${jsonLdScripts(seo.jsonLd)}`;
}

/**
 * /ads.txt（AdSense の販売者認証）。ads.txt の pub ID は "ca-" 無しの形式。
 * f08c47fec0942fa0 は Google の固定の認証機関 ID。ADSENSE_CLIENT が無ければ空。
 */
export function renderAdsTxt(adsenseClient: string): string {
  if (!adsenseClient) return "";
  const pub = adsenseClient.replace(/^ca-/, "");
  return `google.com, ${pub}, DIRECT, f08c47fec0942fa0\n`;
}

/**
 * "/" は静的アセット（app/dist/index.html）をそのまま返せないため、Worker が
 * <!--ENV_INJECT--> プレースホルダをこの HTML に差し替えて配信する（run_worker_first）。
 * gtag/AdSense のローダーと canonical / OGP / JSON-LD に加えて window.__ANJU_ENV__ を埋め込み、
 * main.ts からランタイムの GA_MEASUREMENT_ID / ADSENSE_CLIENT を読めるようにする。
 * gtag.js・adsbygoogle.js は Google 側が配信内容を継続的に更新するため SRI（integrity 属性）を
 * 意図的に付けていない（付けると Google 側の更新のたびにハッシュ不一致で読み込み不能になる）。
 */
export function envInjectSnippet(opts: PageOptions, lang: Lang = DEFAULT_LANG): string {
  const m = t(lang);
  const envJson = JSON.stringify({
    gaMeasurementId: opts.gaMeasurementId,
    adsenseClient: opts.adsenseClient,
  }).replace(/</g, "\\u003c");
  const seo = seoHeadTags({
    siteOrigin: opts.siteOrigin,
    path: "/",
    lang,
    title: m.siteTitle,
    description: m.siteDescription,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: m.brand,
        url: opts.siteOrigin,
        inLanguage: lang,
      },
    ],
  });
  return `${seo}
    ${gtagSnippet(opts.gaMeasurementId)}
    ${adsenseLoaderSnippet(opts.adsenseClient)}
    <script>window.__ANJU_ENV__=${envJson};</script>`;
}

/** サイドバー下部・詳細ページ本文下の広告枠。ADSENSE_CLIENT が無ければ同サイズの空 div。 */
export function adSlot(adsenseClient: string, slotId: string): string {
  if (!adsenseClient) return `<div class="ad-slot ad-slot-empty" aria-hidden="true"></div>`;
  return `<ins class="adsbygoogle ad-slot" style="display:block" data-ad-client="${escapeHtml(adsenseClient)}" data-ad-slot="${escapeHtml(slotId)}" data-ad-format="auto" data-full-width-responsive="true"></ins>`;
}

const FONT_CSS_URL =
  "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Noto+Sans+JP:wght@400;500;700&display=swap";

/**
 * index.html と SSR ページで同じフォントを読む。ここを唯一の定義にしてズレを防ぐ。
 * Google Fonts の CSS は cross-origin でレンダリングをブロックするため、media="print" で
 * 非ブロッキングに取得して onload で all に切り替える。--font の後段に system-ui 等の
 * フォールバックがあるので、切り替わるまでの表示はそれで賄う。JS 無効時は noscript が読む。
 */
export const FONT_LINKS = `<link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
  <link rel="stylesheet" href="${FONT_CSS_URL}" media="print" onload="this.media='all'" />
  <noscript><link rel="stylesheet" href="${FONT_CSS_URL}" /></noscript>`;

/** FONT_LINKS と同じ扱い。app/public/index.html にも同じ 5 行を直書きしてある。 */
export const ICON_LINKS = `<link rel="icon" type="image/svg+xml" href="/favicon.svg" />
  <link rel="icon" type="image/png" sizes="48x48" href="/favicon.png" />
  <link rel="icon" href="/favicon.ico" sizes="32x32" />
  <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
  <link rel="manifest" href="/site.webmanifest" />`;

const THEME_COLOR_METAS = `<meta name="theme-color" content="#eef1f5" media="(prefers-color-scheme: light)" />
  <meta name="theme-color" content="#0b1220" media="(prefers-color-scheme: dark)" />`;

/** 初回描画で system テーマの色に切り替わるちらつきを避けるため、CSS より先に data-theme を確定させる。 */
const THEME_BOOT_SCRIPT = `<script>(function(){try{var t=localStorage.getItem("anju:theme");if(t==="light"||t==="dark")document.documentElement.dataset.theme=t;}catch(e){}})();</script>`;

/** 現在のパスのまま言語だけを差し替えるリンク。切り替え先は必ず lang= を明示する
 *  （明示しないと、ブラウザの言語設定が英語の人は日本語へ戻せない）。 */
export function langSwitchLink(path: string, lang: Lang): string {
  const next = otherLang(lang);
  return `<a class="lang-btn" href="${escapeHtml(withLang(path, next, true))}" hreflang="${next}" lang="${next}" aria-label="${escapeHtml(t(lang).switchLanguage)}">${escapeHtml(LANG_NAMES[next])}</a>`;
}

export interface ShellInput {
  title: string;
  description: string;
  /** canonical のパス（言語指定は付けない）。 */
  path: string;
  body: string;
  opts: PageOptions;
  lang: Lang;
  ogType?: "website" | "article";
  jsonLd?: object[];
  /** 404 のように検索結果に載せたくないページだけ true。 */
  noindex?: boolean;
}

export function pageShell(input: ShellInput): string {
  const { lang } = input;
  const m = t(lang);
  const href = (to: string): string => escapeHtml(withLang(to, lang, input.opts.explicitLang ?? false));
  const seo = seoHeadTags({
    siteOrigin: input.opts.siteOrigin,
    path: input.path,
    lang,
    title: input.title,
    description: input.description,
    ogType: input.ogType ?? "article",
    jsonLd: input.jsonLd ?? [],
  });
  return `<!doctype html>
<html lang="${lang}">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(input.title)}</title>
  <meta name="description" content="${escapeHtml(input.description)}" />
  ${input.noindex ? `<meta name="robots" content="noindex" />` : ""}
  ${seo}
  ${THEME_COLOR_METAS}
  ${FONT_LINKS}
  ${ICON_LINKS}
  <link rel="stylesheet" href="/style.css" />
  ${THEME_BOOT_SCRIPT}
  ${gtagSnippet(input.opts.gaMeasurementId)}
  ${adsenseLoaderSnippet(input.opts.adsenseClient)}
</head>
<body class="doc-page">
  <header class="doc-topbar">
    <a class="brand" href="${href("/")}">${ICON_LOGO}<span class="brand-name">${escapeHtml(m.brand)}</span></a>
    <span class="doc-topbar-actions">
      ${langSwitchLink(input.path, lang)}
      <a class="btn-ghost" href="${href("/")}">${escapeHtml(m.openMap)}</a>
    </span>
  </header>
  <main class="doc-main">${input.body}</main>
  <footer class="doc-footer"><a href="${href("/lp")}">${escapeHtml(m.aboutSite)}</a>${escapeHtml(m.sep)}<a href="${href("/guide")}">${escapeHtml(m.crumbGuide)}</a>${escapeHtml(m.sep)}<a href="${href("/about")}">${escapeHtml(m.sourcesAndDisclaimer)}</a>${escapeHtml(m.sep)}<a href="${href("/privacy")}">${escapeHtml(m.privacyPolicy)}</a>${escapeHtml(m.sep)}<a href="${href("/contact")}">${escapeHtml(m.contactNav)}</a></footer>
</body>
</html>`;
}

/** index.html の <head> のうち、言語で入れ替える範囲を挟むマーカー。 */
export const I18N_HEAD_START = "<!--I18N_HEAD_START-->";
export const I18N_HEAD_END = "<!--I18N_HEAD_END-->";

function appHeadHtml(lang: Lang): string {
  const m = t(lang);
  return `<title>${escapeHtml(m.siteTitle)}</title>
  <meta name="description" content="${escapeHtml(m.siteDescription)}" />`;
}

/**
 * 静的アセットの index.html を、その言語のページとして仕上げる（Worker が "/" で通す）。
 * canonical・hreflang・OGP は envInjectSnippet が入れるので、ここは <title> と description、
 * それに html の lang だけを差し替える。マーカーが見つからなければ元の HTML をそのまま返す。
 */
export function localizeAppHtml(rawHtml: string, lang: Lang): string {
  const start = rawHtml.indexOf(I18N_HEAD_START);
  const end = rawHtml.indexOf(I18N_HEAD_END);
  const head =
    start === -1 || end < start
      ? rawHtml
      : rawHtml.slice(0, start) + appHeadHtml(lang) + rawHtml.slice(end + I18N_HEAD_END.length);
  return head.replace(`<html lang="${DEFAULT_LANG}"`, `<html lang="${lang}"`);
}

/** 一覧・description で使う指標の一覧表記。null は「—」、津波の内陸判定だけ別扱い。 */
export function metricText(muni: Muni, metric: MetricKey, lang: Lang): string {
  if (metric === "tsunami" && muni.flags.includes("inland-no-tsunami")) return t(lang).notDesignated;
  return formatMetricValue(muni, metric, lang);
}

function metricSummary(muni: Muni, lang: Lang): MetricSummary {
  const of = (metric: MetricKey): string => metricText(muni, metric, lang);
  return {
    flood: of("flood"),
    tsunami: of("tsunami"),
    sediment: of("sediment"),
    quake: of("quake"),
    landPrice: of("landPrice"),
    tempJan: of("tempJan"),
    tempAug: of("tempAug"),
    distTokyo: of("distTokyo"),
  };
}

function neighborsSection(neighbors: Neighbor[], lang: Lang, explicitLang: boolean): string {
  if (neighbors.length === 0) return "";
  const m = t(lang);
  const items = neighbors
    .map(
      (n) => `<li class="neighbor-item">
        <a href="${escapeHtml(withLang(`/m/${n.muni.code}`, lang, explicitLang))}"><span class="neighbor-pref">${escapeHtml(prefLabel(n.muni.pref, lang))}</span>${escapeHtml(n.muni.name)}</a>
        <span class="neighbor-dist">${n.distKm.toFixed(1)} km</span>
      </li>`,
    )
    .join("");
  return `<section class="md-block">
      <h2>${escapeHtml(m.neighborsHeading)}</h2>
      <ul class="neighbor-list">${items}</ul>
      <p class="md-note">${escapeHtml(m.neighborsNote)}</p>
    </section>`;
}

export function renderMuniPage(
  muni: Muni,
  manifest: Manifest,
  opts: PageOptions,
  rank?: RankIndex,
  neighbors?: Neighbor[],
  lang: Lang = DEFAULT_LANG,
): string {
  const m = t(lang);
  const explicitLang = opts.explicitLang ?? false;
  const prefCode = prefCodeOf(muni.code);
  const prefPath = `/pref/${prefCode}`;
  const path = `/m/${muni.code}`;
  const prefName = prefLabel(muni.pref, lang);
  const fullName = muniLabel(muni.pref, muni.name, lang);
  const crumbs: Crumb[] = [
    { name: m.crumbHome, path: "/" },
    { name: prefName, path: prefPath },
    { name: muni.name, path },
  ];

  const body = `<div class="doc-card">
      ${breadcrumbHtml(crumbs, lang, explicitLang)}
      ${renderMuniDetailBody(muni, manifest, rank, lang)}
      ${neighborsSection(neighbors ?? [], lang, explicitLang)}
      ${rankAndGuideLinks(`/${prefCode}`, m.rankInPrefHeading(prefName), lang, explicitLang)}
      <p class="back-to-map"><a class="btn-primary" href="${escapeHtml(withLang(`/?sel=${muni.code}`, lang, explicitLang))}">${escapeHtml(m.backToMap)}</a></p>
      <p class="to-pref"><a href="${escapeHtml(withLang(prefPath, lang, explicitLang))}">${escapeHtml(m.toPrefList(prefName))}</a></p>
      <section class="ad-below">${adSlot(opts.adsenseClient, "muni-detail-below")}</section>
    </div>
    ${muniTrackingSnippet(opts.gaMeasurementId, muni.code)}
    <script defer src="/home.js"></script>`;

  return pageShell({
    title: m.muniPageTitle(fullName),
    description: m.muniPageDescription(fullName, metricSummary(muni, lang)),
    path,
    body,
    opts,
    lang,
    jsonLd: [
      breadcrumbJsonLd(opts.siteOrigin, crumbs),
      {
        "@context": "https://schema.org",
        "@type": "Place",
        name: `${muni.pref}${muni.name}`,
        address: {
          "@type": "PostalAddress",
          addressRegion: muni.pref,
          addressLocality: muni.name,
          addressCountry: "JP",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: muni.lonlat[1],
          longitude: muni.lonlat[0],
        },
        url: `${opts.siteOrigin}${withLang(path, lang)}`,
      },
    ],
  });
}

/** 都道府県ハブの一覧に並べる指標。METRICS の format をそのまま使い、表記を詳細ページと揃える。 */
const PREF_TABLE_METRICS: MetricKey[] = ["flood", "quake", "landPrice", "tempJan", "tempAug"];

export function renderPrefPage(
  pref: PrefEntry,
  manifest: Manifest,
  opts: PageOptions,
  lang: Lang = DEFAULT_LANG,
): string {
  const m = t(lang);
  const metrics = getMetrics(lang);
  const explicitLang = opts.explicitLang ?? false;
  const path = `/pref/${pref.code}`;
  const prefName = prefLabel(pref.name, lang);
  const count = pref.munis.length;
  const crumbs: Crumb[] = [
    { name: m.crumbHome, path: "/" },
    { name: prefName, path },
  ];

  // 見出しは短縮ラベル（MetricDef.short）。760px の本文幅に 6 列を収めるため。
  // 正式名称は abbr の title に残す。
  const head = PREF_TABLE_METRICS.map(
    (k) =>
      `<th scope="col"><abbr title="${escapeHtml(metrics[k].label)}">${escapeHtml(metrics[k].short)}</abbr></th>`,
  ).join("");
  const rows = pref.munis
    .map(
      (muni) => `<tr class="pref-row">
        <th scope="row"><a href="${escapeHtml(withLang(`/m/${muni.code}`, lang, explicitLang))}">${escapeHtml(muni.name)}</a></th>
        ${PREF_TABLE_METRICS.map((k) => `<td>${escapeHtml(metricText(muni, k, lang))}</td>`).join("")}
      </tr>`,
    )
    .join("");

  // 地図側の絞り込みはデータの値（日本語の都道府県名）で行うので、リンクの pref は訳さない。
  const mapHref = withLang(`/?pref=${encodeURIComponent(pref.name)}`, lang, explicitLang);

  const body = `<div class="doc-card">
      ${breadcrumbHtml(crumbs, lang, explicitLang)}
      <article class="about pref-hub">
        <h1>${escapeHtml(m.prefHeading(prefName, count))}</h1>
        <p>${escapeHtml(m.prefIntro(prefName, count))}</p>
        <div class="pref-table-wrap">
          <table class="pref-table">
            <thead><tr><th scope="col">${escapeHtml(m.prefColumnMuni)}</th>${head}</tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
        <p class="back-to-map"><a class="btn-primary" href="${escapeHtml(mapHref)}">${escapeHtml(m.prefViewOnMap(prefName))}</a></p>
        <h2>${escapeHtml(m.rankInPrefHeading(prefName))}</h2>
        ${rankNavHtml(`/${pref.code}`, lang, explicitLang, m.rankInPrefHeading(prefName))}
        <p class="md-note">${escapeHtml(m.prefBuiltNote(manifest.builtAt))}</p>
      </article>
    </div>`;

  return pageShell({
    title: m.prefPageTitle(prefName),
    description: m.prefPageDescription(prefName, count),
    path,
    body,
    opts,
    lang,
    jsonLd: [breadcrumbJsonLd(opts.siteOrigin, crumbs)],
  });
}

/**
 * 12 指標のランキングへの導線。suffix は都道府県版なら "/{2桁}"、全国版なら ""。
 * 都道府県ハブとトップのクローラー向けセクションで共有する。
 */
function rankNavHtml(suffix: string, lang: Lang, explicitLang: boolean, ariaLabel: string): string {
  const metrics = getMetrics(lang);
  const links = METRIC_ORDER.map(
    (key) =>
      `<a href="${escapeHtml(withLang(`/rank/${key}${suffix}`, lang, explicitLang))}" title="${escapeHtml(metrics[key].label)}">${escapeHtml(metrics[key].short)}</a>`,
  ).join("");
  return `<nav class="pref-nav" aria-label="${escapeHtml(ariaLabel)}">${links}</nav>`;
}

/**
 * 12 指標のランキング（suffix で都道府県版 / 全国版）と、ランキング索引・ガイドへの導線。
 * 詳細ページと 404 ページで共有し、564 件の都道府県別ランキングにクローラが辿り着けるようにする。
 */
function rankAndGuideLinks(suffix: string, heading: string, lang: Lang, explicitLang: boolean): string {
  const m = t(lang);
  return `<section class="rank-links">
        <h2>${escapeHtml(heading)}</h2>
        ${rankNavHtml(suffix, lang, explicitLang, heading)}
        <p class="md-note"><a href="${escapeHtml(withLang("/rank", lang, explicitLang))}">${escapeHtml(m.rankIndexHeading)}</a>${escapeHtml(m.sep)}<a href="${escapeHtml(withLang("/guide", lang, explicitLang))}">${escapeHtml(m.guideIndexHeading)}</a></p>
      </section>`;
}

/** 47 都道府県への導線。404 ページとトップのクローラー向けセクションで共有する。 */
function prefNavHtml(prefs: PrefEntry[], lang: Lang, explicitLang: boolean): string {
  const links = prefs
    .map(
      (p) =>
        `<a href="${escapeHtml(withLang(`/pref/${p.code}`, lang, explicitLang))}">${escapeHtml(prefLabel(p.name, lang))}</a>`,
    )
    .join("");
  return `<nav class="pref-nav" aria-label="${escapeHtml(t(lang).findByPref)}">${links}</nav>`;
}

export function renderNotFoundPage(
  prefs: PrefEntry[],
  opts: PageOptions,
  lang: Lang = DEFAULT_LANG,
): string {
  const m = t(lang);
  const explicitLang = opts.explicitLang ?? false;
  const body = `<div class="doc-card">
      <article class="about">
        <h1>${escapeHtml(m.notFoundHeading)}</h1>
        <p>${escapeHtml(m.notFoundLead)}</p>
        <p class="back-to-map"><a class="btn-primary" href="${escapeHtml(withLang("/", lang, explicitLang))}">${escapeHtml(m.notFoundSearchMap)}</a></p>
        <h2>${escapeHtml(m.findByPref)}</h2>
        ${prefNavHtml(prefs, lang, explicitLang)}
        ${rankAndGuideLinks("", m.rankIndexHeading, lang, explicitLang)}
      </article>
    </div>`;
  return pageShell({
    title: m.notFoundPageTitle,
    description: m.notFoundDescription,
    path: "/404",
    body,
    opts,
    lang,
    noindex: true,
  });
}

/**
 * トップのクローラー向けセクション。地図が全面を占めるデザインのため、結果一覧パネルの
 * 最下部（#results-body の末尾）に置く。視覚的に隠していない。
 */
export function renderTopSeoSection(
  prefs: PrefEntry[],
  lang: Lang = DEFAULT_LANG,
  explicitLang = false,
): string {
  const m = t(lang);
  const total = prefs.reduce((sum, p) => sum + p.munis.length, 0);
  const aboutLink = `<a href="${escapeHtml(withLang("/about", lang, explicitLang))}">${escapeHtml(m.sourcesAndDisclaimer)}</a>`;
  return `<section class="seo-intro" aria-label="${escapeHtml(m.topSeoAria)}">
      <h1>${escapeHtml(m.topSeoH1)}</h1>
      <p>${escapeHtml(m.topSeoP1(formatNumber(total, lang)))}</p>
      <p>${escapeHtml(m.topSeoP2)}</p>
      <p>${escapeHtml(m.topSeoP3Before)}${aboutLink}${escapeHtml(m.topSeoP3After)}</p>
      <p class="seo-links">${internalLink("/privacy", m.privacyPolicy, lang, explicitLang)}${escapeHtml(m.sep)}${internalLink("/contact", m.contactNav, lang, explicitLang)}</p>
      <h2>${escapeHtml(m.findByPref)}</h2>
      ${prefNavHtml(prefs, lang, explicitLang)}
      <h2><a href="${escapeHtml(withLang("/rank", lang, explicitLang))}">${escapeHtml(m.rankIndexHeading)}</a></h2>
      ${rankNavHtml("", lang, explicitLang, m.rankIndexHeading)}
      <h2><a href="${escapeHtml(withLang("/guide", lang, explicitLang))}">${escapeHtml(m.guideIndexHeading)}</a></h2>
      <p>${escapeHtml(m.guideIndexIntro)}</p>
    </section>`;
}

export function renderAboutPage(manifest: Manifest, opts: PageOptions, lang: Lang = DEFAULT_LANG): string {
  const m = t(lang);
  const sourcesHtml = manifest.sources
    .map(
      (s) => `<li class="source-item">
        <p class="source-title"><a href="${escapeHtml(s.url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(s.title)} ${ICON_EXTERNAL}</a></p>
        <p class="source-meta">${escapeHtml(m.sourceMeta(s.publisher, s.retrievedAt) + m.sep + s.license)}</p>
        <p class="src-method">${escapeHtml(s.method)}</p>
        ${isStale(s) ? `<p class="stale-note">${escapeHtml(m.staleNote)}</p>` : ""}
      </li>`,
    )
    .join("");

  const crumbs: Crumb[] = [
    { name: m.crumbHome, path: "/" },
    { name: m.aboutTitle, path: "/about" },
  ];

  // 英語表示では「何を訳し、何を訳さないか」を明示する（市区町村名と出典名は原語のまま）。
  const languageNote = lang === "en" ? `<p>${escapeHtml(m.aboutLanguageNote)}</p>` : "";

  const body = `<div class="doc-card">
      ${breadcrumbHtml(crumbs, lang, opts.explicitLang ?? false)}
      <article class="about">
        <h1>${escapeHtml(m.aboutTitle)}</h1>
        <p>${escapeHtml(m.aboutDisclaimer)}</p>
        <p>${escapeHtml(m.aboutHazardNote)}</p>
        <p>${escapeHtml(m.aboutAnalytics)}</p>
        ${languageNote}
        <h2>${escapeHtml(m.aboutContactHeading)}</h2>
        <p>${escapeHtml(m.aboutContactBody)}${internalLink("/contact", m.contactNav, lang, opts.explicitLang ?? false)}${escapeHtml(m.aboutContactBodyAfter)}${internalLink("/privacy", m.privacyPolicy, lang, opts.explicitLang ?? false)}${escapeHtml(m.aboutContactBodyEnd)}</p>
        <h2>${escapeHtml(m.aboutSourcesHeading(manifest.muniCount, manifest.builtAt))}</h2>
        <ul class="sources">${sourcesHtml}</ul>
      </article>
    </div>`;

  return pageShell({
    title: m.aboutPageTitle,
    description: m.aboutPageDescription,
    path: "/about",
    body,
    opts,
    lang,
    jsonLd: [breadcrumbJsonLd(opts.siteOrigin, crumbs)],
  });
}

/**
 * 問い合わせフォーム。JS 無しの HTML フォームとして完結させる（POST /contact）。
 * 再描画のために入力値をそのまま受け取るので、値は必ず escapeHtml を通す。
 */
export interface ContactFormView {
  values: { name: string; email: string; topic: string; message: string };
  /** parseContactForm が返した検証エラー。空なら初回表示。 */
  errors: string[];
  /** Turnstile のサイトキー。空ならウィジェットを出さない。 */
  turnstileSiteKey: string;
}

export function renderContactPage(
  view: ContactFormView,
  opts: PageOptions,
  lang: Lang = DEFAULT_LANG,
): string {
  const m = t(lang);
  const explicitLang = opts.explicitLang ?? false;
  const crumbs: Crumb[] = [
    { name: m.crumbHome, path: "/" },
    { name: m.contactTitle, path: "/contact" },
  ];

  const errorsHtml =
    view.errors.length === 0
      ? ""
      : `<div class="form-errors" role="alert">
        <p class="form-errors-head">${escapeHtml(m.contactErrorsHeading)}</p>
        <ul>${view.errors.map((e) => `<li>${escapeHtml(e)}</li>`).join("")}</ul>
      </div>`;

  const topicOptions = CONTACT_TOPICS.map((topic) => {
    const selected = view.values.topic === topic ? " selected" : "";
    return `<option value="${escapeHtml(topic)}"${selected}>${escapeHtml(m.contactTopicLabels[topic])}</option>`;
  }).join("");

  // サイトキーが無い環境（ローカル開発・未設定）ではウィジェット自体を出さない。
  const turnstileHtml = view.turnstileSiteKey
    ? `<script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer></script>
      <div class="cf-turnstile" data-sitekey="${escapeHtml(view.turnstileSiteKey)}"></div>`
    : "";

  const body = `<div class="doc-card">
      ${breadcrumbHtml(crumbs, lang, explicitLang)}
      <article class="about">
        <h1>${escapeHtml(m.contactTitle)}</h1>
        <p>${escapeHtml(m.contactIntro)}</p>
        <p>${escapeHtml(m.contactPrivacyNoteBefore)}${internalLink("/privacy", m.privacyPolicy, lang, explicitLang)}${escapeHtml(m.contactPrivacyNoteAfter)}</p>
        ${errorsHtml}
        <form class="contact-form" method="post" action="${escapeHtml(withLang("/contact", lang, explicitLang))}">
          <label class="form-row">
            <span class="form-label">${escapeHtml(m.contactNameLabel)}</span>
            <input type="text" name="name" maxlength="${CONTACT_LIMITS.nameMax}" autocomplete="name" value="${escapeHtml(view.values.name)}" />
          </label>
          <label class="form-row">
            <span class="form-label">${escapeHtml(m.contactEmailLabel)}</span>
            <input type="email" name="email" required maxlength="${CONTACT_LIMITS.emailMax}" autocomplete="email" value="${escapeHtml(view.values.email)}" />
          </label>
          <label class="form-row">
            <span class="form-label">${escapeHtml(m.contactTopicLabel)}</span>
            <select name="topic">${topicOptions}</select>
          </label>
          <label class="form-row">
            <span class="form-label">${escapeHtml(m.contactMessageLabel)}</span>
            <textarea name="message" required rows="8" minlength="${CONTACT_LIMITS.messageMin}" maxlength="${CONTACT_LIMITS.messageMax}">${escapeHtml(view.values.message)}</textarea>
            <span class="form-hint">${escapeHtml(m.contactMessageHint)}</span>
          </label>
          <div style="position:absolute;left:-9999px" aria-hidden="true">
            <label>Website<input type="text" name="${escapeHtml(HONEYPOT_FIELD)}" tabindex="-1" autocomplete="off" /></label>
          </div>
          ${turnstileHtml}
          <button type="submit" class="btn-primary">${escapeHtml(m.contactSubmit)}</button>
        </form>
      </article>
    </div>`;

  return pageShell({
    title: m.contactPageTitle,
    description: m.contactPageDescription,
    path: "/contact",
    body,
    opts,
    lang,
    jsonLd: [breadcrumbJsonLd(opts.siteOrigin, crumbs)],
  });
}

/** 送信完了・レート制限・設定不足・送信失敗に共通の 1 枚もの。検索結果には載せない。 */
export function renderContactResultPage(
  heading: string,
  message: string,
  opts: PageOptions,
  lang: Lang = DEFAULT_LANG,
): string {
  const m = t(lang);
  const body = `<div class="doc-card">
      <article class="about">
        <h1>${escapeHtml(heading)}</h1>
        <p>${escapeHtml(message)}</p>
        <p>${internalLink("/", m.contactBackToTop, lang, opts.explicitLang ?? false)}</p>
      </article>
    </div>`;

  return pageShell({
    title: `${heading}${m.sep}${m.brand}`,
    description: message,
    path: "/contact",
    body,
    opts,
    lang,
    noindex: true,
  });
}

/** プライバシーポリシーの改定日。文言と一緒に動かすのでここに置く。 */
export const PRIVACY_UPDATED_AT = "2026-09-10";

export function renderPrivacyPage(opts: PageOptions, lang: Lang = DEFAULT_LANG): string {
  const m = t(lang);
  const explicitLang = opts.explicitLang ?? false;
  const crumbs: Crumb[] = [
    { name: m.crumbHome, path: "/" },
    { name: m.privacyTitle, path: "/privacy" },
  ];
  const contactLink = internalLink("/contact", m.contactNav, lang, explicitLang);

  const body = `<div class="doc-card">
      ${breadcrumbHtml(crumbs, lang, explicitLang)}
      <article class="about">
        <h1>${escapeHtml(m.privacyTitle)}</h1>
        <p class="source-meta">${escapeHtml(m.privacyUpdated(PRIVACY_UPDATED_AT))}</p>

        <h2>${escapeHtml(m.privacyOperatorHeading)}</h2>
        <p>${escapeHtml(m.privacyOperatorBody)}${contactLink}${escapeHtml(m.privacyOperatorBodyAfter)}</p>

        <h2>${escapeHtml(m.privacyCollectHeading)}</h2>
        <ul>
          <li>${escapeHtml(m.privacyCollectGa)}</li>
          <li>${escapeHtml(m.privacyCollectAds)}</li>
          <li>${escapeHtml(m.privacyCollectContact)}</li>
          <li>${escapeHtml(m.privacyCollectLocal)}</li>
        </ul>

        <h2>${escapeHtml(m.privacyPurposeHeading)}</h2>
        <p>${escapeHtml(m.privacyPurposeBody)}</p>

        <h2>${escapeHtml(m.privacyThirdPartyHeading)}</h2>
        <p>${escapeHtml(m.privacyThirdPartyBody)}</p>
        <ul>
          <li>${externalLink("https://policies.google.com/technologies/partner-sites", m.privacyLinkGooglePartner)}</li>
          <li>${externalLink("https://policies.google.com/technologies/ads", m.privacyLinkGoogleAds)}</li>
          <li>${externalLink("https://resend.com/legal/privacy-policy", m.privacyLinkResend)}</li>
        </ul>

        <h2>${escapeHtml(m.privacyOptOutHeading)}</h2>
        <p>${escapeHtml(m.privacyOptOutBody)}</p>
        <ul>
          <li>${externalLink("https://tools.google.com/dlpage/gaoptout", m.privacyLinkGaOptOut)}</li>
          <li>${externalLink("https://adssettings.google.com", m.privacyLinkAdsSettings)}</li>
        </ul>

        <h2>${escapeHtml(m.privacyRetentionHeading)}</h2>
        <ul>
          <li>${escapeHtml(m.privacyRetentionGa)}</li>
          <li>${escapeHtml(m.privacyRetentionContact)}</li>
        </ul>

        <h2>${escapeHtml(m.privacyDisclaimerHeading)}</h2>
        <p>${escapeHtml(m.privacyDisclaimerBefore)}${internalLink("/about", m.sourcesAndDisclaimer, lang, explicitLang)}${escapeHtml(m.privacyDisclaimerAfter)}</p>
      </article>
    </div>`;

  return pageShell({
    title: m.privacyPageTitle,
    description: m.privacyPageDescription,
    path: "/privacy",
    body,
    opts,
    lang,
    jsonLd: [breadcrumbJsonLd(opts.siteOrigin, crumbs)],
  });
}

/** 指標の値を一覧・検索候補で使う短い文字列に。null は「—」。 */
export function formatMetricValue(muni: Muni, metric: MetricKey, lang: Lang = DEFAULT_LANG): string {
  const def = getMetrics(lang)[metric];
  const value = def.get(muni);
  return value === null ? DASH : def.format(value);
}
